import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useMeAI } from "@/context/MeAIContext";
import {
  Smartphone,
  Tablet,
  Tv,
  Speaker,
  Watch,
  Cpu,
  Bot,
  ScanLine,
  Stethoscope,
  Plus,
  Radar,
} from "lucide-react";

const KIND_ICON = {
  phone: Smartphone,
  tablet: Tablet,
  desktop: Cpu,
  smart_tv: Tv,
  smart_speaker: Speaker,
  wearable: Watch,
  medical_capsule: Stethoscope,
  robot: Bot,
  iot: Cpu,
  imaging: ScanLine,
  lab_instrument: ScanLine,
  ehr_endpoint: Cpu,
  unknown: Cpu,
};

export default function DevicesPage() {
  const { t } = useLanguage();
  const { being, refresh, claim, release } = useMeAI();
  const [devices, setDevices] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [error, setError] = useState("");
  const [discovered, setDiscovered] = useState(null);
  const [discovering, setDiscovering] = useState(false);

  async function load() {
    try {
      const [d, p] = await Promise.all([api.get("/devices"), api.get("/devices/protocols")]);
      setDevices(d.data);
      setProtocols(p.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const activeId = being?.current_device_id;

  async function onClaim(id) {
    await claim(id);
    await load();
  }
  async function onRelease() {
    await release();
    await load();
  }
  async function onDiscover() {
    setDiscovering(true);
    try {
      const { data } = await api.post("/devices/discover", {});
      setDiscovered(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDiscovering(false);
    }
  }

  return (
    <div className="space-y-8" data-testid="devices-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            ME AI · {t.meai.devices}
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
            {t.meai.title}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">{t.meai.sub}</p>
          <p className="mt-3 text-xs italic text-slate-400">{t.meai.handoverHint}</p>
        </div>
        <button
          type="button"
          onClick={onDiscover}
          disabled={discovering}
          className="mios-btn mios-btn-ghost"
          data-testid="discover-button"
        >
          <Radar className="h-4 w-4" /> {t.meai.discover}
        </button>
      </header>

      <BeingCard being={being} t={t} onRelease={onRelease} />

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section data-testid="devices-list" className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {devices.map((d) => {
          const Icon = KIND_ICON[d.kind] || Cpu;
          const isActive = d.id === activeId;
          return (
            <div
              key={d.id}
              className={`mios-card transition-colors ${isActive ? "ring-2 ring-[color:var(--mios-brand)]" : ""}`}
              data-testid={`device-card-${d.id}`}
            >
              <div className="mios-card-body">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
                      <Icon className="h-5 w-5 text-slate-700" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{d.name}</div>
                      <div className="text-[11px] uppercase tracking-[0.06em] text-slate-500">
                        {t.meai.kinds[d.kind] || d.kind}
                      </div>
                    </div>
                  </div>
                  {isActive && (
                    <span className="mios-pill mios-pill-normal" data-testid={`device-active-${d.id}`}>
                      {t.meai.activeOn}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {d.protocols?.map((p) => (
                    <span key={p} className="mios-pill mios-pill-low mono">{p}</span>
                  ))}
                </div>
                <div className="mt-4">
                  {isActive ? (
                    <button
                      type="button"
                      onClick={onRelease}
                      className="mios-btn mios-btn-ghost w-full"
                      data-testid={`release-${d.id}`}
                    >
                      {t.meai.release}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onClaim(d.id)}
                      className="mios-btn mios-btn-primary w-full"
                      data-testid={`claim-${d.id}`}
                    >
                      {t.meai.claim}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <ProtocolMatrix protocols={protocols} t={t} />

      {discovered && (
        <div className="mios-card" data-testid="discovered-card">
          <div className="mios-card-header"><div className="mios-card-title">{t.meai.discover}</div><div className="mios-card-sub">{discovered.length}</div></div>
          <div className="mios-card-body">
            {discovered.length === 0 ? (
              <div className="text-sm text-slate-400">
                — (No native bridge installed; install a Bluetooth/BLE/USB/DICOM/Matter bridge to enable real discovery.)
              </div>
            ) : (
              <ul className="space-y-1 text-sm">
                {discovered.map((d, i) => (
                  <li key={i} className="mono text-slate-700">{d.transport} · {d.name} · {d.address}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BeingCard({ being, t, onRelease }) {
  const isOnline = being?.presence === "online";
  const currentDevice = being?.current_device;
  return (
    <div className="mios-card" data-testid="being-card">
      <div className="mios-card-body flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`relative flex h-10 w-10 items-center justify-center rounded-full ${isOnline ? "bg-emerald-500" : "bg-slate-300"} text-white`}>
            <span className="font-semibold">ME</span>
            <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${isOnline ? "bg-emerald-400" : "bg-slate-400"}`} />
          </div>
          <div>
            <div className="text-sm font-medium text-slate-900">ME AI</div>
            <div className="text-[11px] text-slate-500">
              {t.meai.presence}: {isOnline ? t.meai.online : t.meai.offline}
              {currentDevice && (
                <> · {t.meai.activeOn} <span className="font-medium text-slate-700">{currentDevice.name}</span></>
              )}
            </div>
          </div>
        </div>
        {currentDevice && (
          <button type="button" className="mios-btn mios-btn-ghost" onClick={onRelease} data-testid="being-release-button">
            {t.meai.release}
          </button>
        )}
      </div>
    </div>
  );
}

function ProtocolMatrix({ protocols, t }) {
  const groups = useMemo(() => {
    const ready = protocols.filter((p) => p.available);
    const bridge = protocols.filter((p) => !p.available && p.requires_native_bridge);
    const config = protocols.filter((p) => !p.available && !p.requires_native_bridge);
    return { ready, bridge, config };
  }, [protocols]);

  return (
    <div className="mios-card" data-testid="protocols-card">
      <div className="mios-card-header">
        <div className="mios-card-title">{t.meai.protocols}</div>
        <div className="mios-card-sub">{protocols.length}</div>
      </div>
      <div className="mios-card-body grid gap-4 md:grid-cols-3">
        <ProtocolGroup label={t.meai.available} items={groups.ready} tone="normal" />
        <ProtocolGroup label={t.meai.needsBridge} items={groups.bridge} tone="warning" />
        <ProtocolGroup label={t.meai.needsConfig} items={groups.config} tone="low" />
      </div>
    </div>
  );
}

function ProtocolGroup({ label, items, tone }) {
  const cls = tone === "normal" ? "mios-pill-normal" : tone === "warning" ? "mios-pill-warning" : "mios-pill-low";
  return (
    <div>
      <div className="mios-card-sub mb-2">{label} · {items.length}</div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400">—</div>
      ) : (
        <ul className="space-y-2">
          {items.map((p) => (
            <li key={p.name} className="flex items-center justify-between text-sm">
              <span className="capitalize text-slate-700">{p.name.replaceAll("_", " ")}</span>
              <span className={`mios-pill ${cls}`}>{p.capabilities.slice(0, 2).join(" · ")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
